import { clearParentReferralPendingDestination } from "#/src/utils/parentReferralIntent";
import { setEntityId, setEntityType } from "@classdojo/log-client";
import { setUser } from "@web-monorepo/telemetry";
import { paths } from "@web-monorepo/api-types";
import { fetchApiData } from "#/src/hooks/api/fetcherDefaults";
import { useQuery, queryClient as messagingQueryClient } from "@web-monorepo/fetchers";
import { getEntityType, type SessionData } from "#/src/hooks/api/auth/session";
import queryClient from "#/src/queryClient";
import { setDefaultTimezone } from "#/src/utils/dates";
import { clearPendingFavorite } from "#/src/utils/pendingFavorite";
import { getApiErrorStatus } from "#/src/utils/apiError";

const SESSION_PATH = "/api/marketplace/session";
type Endpoint = paths[typeof SESSION_PATH]["get"];
type Response = Endpoint["responses"][200]["content"]["application/json"];

export const SESSION_QUERY_KEY = "sessionData";
const COPPA_SESSION_QUERY_KEY = "coppaSessionData";

// Opaque id + entity type only, matching teach/home and logClient's entity id/type; no email or name.
const setTelemetryUser = (session: Pick<SessionData, "roles" | "user_details">) => {
  const uid = session.user_details?.uid;
  if (!uid) return;
  setUser({ id: uid, type: getEntityType(session.roles) });
};

const useSessionQueryWithErrorHandler = (queryKey: string, handleSessionError: (error: unknown) => null) => {
  const query = useQuery(
    [queryKey],
    () =>
      fetchApiData<Response>({ method: "GET", path: SESSION_PATH, route: SESSION_PATH })
        .then((data) => {
          setEntityId(data.user_details.uid);
          setEntityType(getEntityType(data.roles));
          setTelemetryUser(data);
          setDefaultTimezone(data.user_details.timezone);
          // Cached as `{ data }` so setSessionQueryData, getSessionData and seeded stories keep their shape.
          return { data };
        })
        .catch(handleSessionError),
    {
      staleTime: Infinity,
    },
  );

  return {
    isInitialLoading: query.isInitialLoading,
    isLoading: query.isLoading,
    isSuccess: query.isSuccess,
    isError: query.isError,
    isFetching: query.isFetching,
    sessionData: query.data?.data as SessionData | undefined,
  };
};

// Marketplace startup intentionally treats all session failures as logged out.
// COPPA uses the scoped fail-closed query below so a broken embedded session
// request cannot be mistaken for consent completion.
export const useSessionQuery = () => useSessionQueryWithErrorHandler(SESSION_QUERY_KEY, () => null);

export const useCoppaSessionQuery = () =>
  useSessionQueryWithErrorHandler(COPPA_SESSION_QUERY_KEY, (error: unknown) => {
    if (getApiErrorStatus(error) === 401) return null;
    throw error;
  });

export const setSessionQueryData = (data: SessionData) => {
  setTelemetryUser(data);
  return queryClient.setQueryData([SESSION_QUERY_KEY], () => ({ data }));
};

/**
 * Force the session to refetch and resolve once settled (`staleTime: Infinity` otherwise pins the
 * cached value until a hard refresh). `refetchType: "all"` also refreshes it when no component is
 * currently observing it, so an `await` reliably waits for the refetch as long as the query exists.
 */
export const invalidateSessionQuery = () =>
  queryClient.invalidateQueries({ queryKey: [SESSION_QUERY_KEY], refetchType: "all" });

export const clearSessionQueryData = () => {
  clearParentReferralPendingDestination();
  setUser(null);
  queryClient.clear();
  // The messaging package (inbox/threads) reads through the @web-monorepo/fetchers singleton
  // QueryClient — mounted by MessagingRoot's nested QueryClientProvider — not this app-level
  // client. Clear it too, or a previous user's cached thread list leaks into the next session.
  messagingQueryClient.clear();
  clearPendingFavorite();
};

export const getSessionData = () =>
  (queryClient.getQueryData([SESSION_QUERY_KEY]) as undefined | { data: SessionData })?.data as SessionData | undefined;

export const updateSessionLocale = (uid: string, locale: string): boolean => {
  const session = getSessionData();
  if (!session || session.user_details.uid !== uid) return false;
  // Apply the server-confirmed PATCH locale immediately to the same account's session.
  // The settings caller then reloads the page to refetch other data under the saved locale.
  setSessionQueryData({ ...session, user_details: { ...session.user_details, locale } });
  return true;
};

/**
 * Ensure the cached session carries a `personProfileId`, refetching only when it's missing so users
 * who already have one skip the round-trip. Callers awaiting this before navigating to a chats route
 * get a settled session (see the call site in useStartMarketplaceChat for why that matters).
 */
export const ensureSessionPersonProfileId = async () => {
  if (getSessionData()?.personProfileId) return;
  await invalidateSessionQuery();
};

export const getSessionUserType = (): "tutor" | "parent" | null => {
  const sessionData = getSessionData();
  if (!sessionData) return null;

  return getEntityType(sessionData.roles) === "teacher" ? "tutor" : "parent";
};
